import {
  CustomerCartIdConsumerInterface,
  CustomerInformationQuery_ConsumerInterface,
  CustomerSessionConsumerInterface,
  FormFieldExtensionKeys_AddressAutocomplete,
  genericBeforeValidate_removeDefaultKeyedError,
  GenericDataSourceIntersectionConsumerInterface,
  GenericForm,
  GenericFormLogicalField,
  GenericFormValidation_YieldEvent,
  ValidationEvent,
} from "../../../Common";

import { CheckoutModel_MyBag } from "../../model";

import { DataMutableSourceShippingClientStorageDataConsumerInterface } from "../data-mutable-source-shipping-client-storage-data-provider";
import { DataMutableSourceShippingSessionStorageDataConsumer } from "../data-mutable-source-shipping-session-storage-data-provider";
import { ReactMyBagLoginAppConsumer } from "../react-my-bag-login-app-provider";

export interface AppMinimumRequirementsFor_ShippingAutofill {
  reactMyBagLoginApp: ReactMyBagLoginAppConsumer;
  customerInformation?: CustomerInformationQuery_ConsumerInterface;
  shippingClientStorageData: DataMutableSourceShippingClientStorageDataConsumerInterface;

  myBagDataSource: GenericDataSourceIntersectionConsumerInterface<CheckoutModel_MyBag>;
  customerSession: CustomerSessionConsumerInterface;
  customerCartId: CustomerCartIdConsumerInterface;
  shippingSessionStorageData: DataMutableSourceShippingSessionStorageDataConsumer;
}

interface ShippingAutofillFormLogicProps<
  Form extends GenericForm,
  App extends AppMinimumRequirementsFor_ShippingAutofill
> {}

export enum FormFieldExtensionKeys_ShippingAutofill {
  HasMigratedAutofills = "_hasMigratedAutofills",
  HasMigratedAutofillsForAFreshUserLogin = "_hasMigratedAutofillsForAFreshUserLogin",
}

export const shippingAutofillFormLogic = <
  Form extends GenericForm,
  App extends AppMinimumRequirementsFor_ShippingAutofill
>(
  props: ShippingAutofillFormLogicProps<Form, App> = {}
): GenericFormLogicalField<Form, App> => ({
  presentation: props,

  produceEffectDependencies: (context) => [
    context.form[
      `${context.name}${FormFieldExtensionKeys_ShippingAutofill.HasMigratedAutofills}`
    ],
    context.form[
      `${context.name}${FormFieldExtensionKeys_ShippingAutofill.HasMigratedAutofillsForAFreshUserLogin}`
    ],
    context.app.shippingClientStorageData.hasInitialized,
    context.app.shippingClientStorageData.lastSelectedSavedAddressId,
    context.app.customerInformation,
    context.app.customerInformation?.status,
    context.app.customerInformation?.data,
    context.app.customerInformation?.data?.addresses,
    context.app.myBagDataSource.latest,
    context.app.myBagDataSource.mostLatestType,
    context.app.customerSession,
    context.app.customerCartId,
    context.app.shippingSessionStorageData,
  ],
  handleEffect: (context) => {
    if (
      typeof document !== "undefined" &&
      document.activeElement instanceof HTMLInputElement
    ) {
      return;
    }
    const thisFieldIsland = context.name.substring(
      0,
      context.name.indexOf("_")
    ) as "shipping" | "billing";

    const {
      hasInitialized: shippingClientStorageData_hasInitialized,
      lastSelected_authoriseDeliveryToBeLeftWithoutSignature,
      lastSelected_isBillingAndShippingTheSame,
    } = context.app.shippingClientStorageData;

    const {
      lastFormattedAutocompletedAddress_forShipping,
      lastFormattedAutocompletedAddress_forBilling,
    } = context.app.shippingSessionStorageData;

    const {
      status: customerInformationQueryStatus,
      data: customerInformationData,
    } = context.app.customerInformation || {};
    const customerInformation_hasInitialized =
      customerInformationQueryStatus === "success" ||
      customerInformationQueryStatus === "error";

    const { latest: myBagResponse, mostLatestType: myBagDataResponseType } =
      context.app.myBagDataSource;

    const {
      hasTheUserLoggedInWhileUsingThisPage_andWereInitiallyNotLoggedInToStartWith,
      userLoggedInWithThisEmailLastIHeard,
    } = context.app.reactMyBagLoginApp;

    const { isCustomerLoggedIn } = context.app.customerSession;
    const { cartId } = context.app.customerCartId;

    const hasInitializedEverythingINeed =
      shippingClientStorageData_hasInitialized &&
      customerInformation_hasInitialized &&
      (myBagDataResponseType === "data" ||
        myBagDataResponseType === "error" ||
        (!cartId && !isCustomerLoggedIn));
    const theCustomerInformationHasLoadedForThisNewLoggedInUser_insteadOfWhateverStaleWasTherePreexisting =
      userLoggedInWithThisEmailLastIHeard &&
      customerInformationData?.email === userLoggedInWithThisEmailLastIHeard;

    const hasMigratedAutofills = context.form[
      `${context.name}${FormFieldExtensionKeys_ShippingAutofill.HasMigratedAutofills}`
    ] as boolean;
    const hasMigratedAutofillsForAFreshUserLogin = context.form[
      `${context.name}${FormFieldExtensionKeys_ShippingAutofill.HasMigratedAutofillsForAFreshUserLogin}`
    ] as boolean;

    const needsAFirstTimeInitialization =
      !hasMigratedAutofills && hasInitializedEverythingINeed;
    const needsANewInitializationBecauseTheUserLoggedIn =
      !!hasTheUserLoggedInWhileUsingThisPage_andWereInitiallyNotLoggedInToStartWith &&
      hasInitializedEverythingINeed &&
      theCustomerInformationHasLoadedForThisNewLoggedInUser_insteadOfWhateverStaleWasTherePreexisting &&
      !hasMigratedAutofillsForAFreshUserLogin;

    const runInitialization =
      needsAFirstTimeInitialization ||
      needsANewInitializationBecauseTheUserLoggedIn;
    if (runInitialization) {
      if (customerInformationData) {
        const yield_setEmail: GenericFormValidation_YieldEvent = {
          name: `emailAddress`,
          validationEvent: ValidationEvent.onChange,
          value: customerInformationData.email || "",
        };
        context.yield.validationEvent(context, yield_setEmail);

        const yield_setFirstName: GenericFormValidation_YieldEvent = {
          name: `${thisFieldIsland}_firstName`,
          validationEvent: ValidationEvent.onChange,
          value: customerInformationData.firstname || "",
        };
        context.yield.validationEvent(context, yield_setFirstName);

        const yield_setLastName: GenericFormValidation_YieldEvent = {
          name: `${thisFieldIsland}_lastName`,
          validationEvent: ValidationEvent.onChange,
          value: customerInformationData.lastname || "",
        };
        context.yield.validationEvent(context, yield_setLastName);
      }

      if (
        lastSelected_authoriseDeliveryToBeLeftWithoutSignature !== undefined &&
        lastSelected_authoriseDeliveryToBeLeftWithoutSignature !== null
      ) {
        const yield_setAuthorizeDeliveryToBeLeftWithoutSignature: GenericFormValidation_YieldEvent =
          {
            name: `authoriseDeliveryToBeLeftWithoutSignature`,
            validationEvent: ValidationEvent.onChange,
            value: !!lastSelected_authoriseDeliveryToBeLeftWithoutSignature,
          };
        context.yield.validationEvent(
          context,
          yield_setAuthorizeDeliveryToBeLeftWithoutSignature
        );
      }

      if (
        lastSelected_isBillingAndShippingTheSame !== undefined &&
        lastSelected_isBillingAndShippingTheSame !== null
      ) {
        const yield_setIsBillingAndShippingTheSame: GenericFormValidation_YieldEvent =
          {
            name: `isBillingAndShippingTheSame`,
            validationEvent: ValidationEvent.onChange,
            value: !!lastSelected_isBillingAndShippingTheSame,
          };
        context.yield.validationEvent(
          context,
          yield_setIsBillingAndShippingTheSame
        );
      }

      if (myBagResponse) {
        if (myBagResponse.email && myBagResponse.email.length > 0) {
          const yield_setEmail: GenericFormValidation_YieldEvent = {
            name: `emailAddress`,
            validationEvent: ValidationEvent.onChange,
            value: myBagResponse.email,
          };
          context.yield.validationEvent(context, yield_setEmail);
        }

        let thisCartAddress;
        const cartAddresses =
          thisFieldIsland === "shipping"
            ? myBagResponse.shipping_addresses &&
              myBagResponse.shipping_addresses.length > 0
              ? myBagResponse.shipping_addresses
              : []
            : myBagResponse.billing_address
            ? [myBagResponse.billing_address]
            : [];
        if (cartAddresses && cartAddresses.length > 0) {
          thisCartAddress = cartAddresses[0];
        }
        if (thisCartAddress) {
          const yield_setFirstName: GenericFormValidation_YieldEvent = {
            name: `${thisFieldIsland}_firstName`,
            validationEvent: ValidationEvent.onChange,
            value: thisCartAddress.firstname,
          };
          context.yield.validationEvent(context, yield_setFirstName);

          const yield_setLastName: GenericFormValidation_YieldEvent = {
            name: `${thisFieldIsland}_lastName`,
            validationEvent: ValidationEvent.onChange,
            value: thisCartAddress.lastname,
          };
          context.yield.validationEvent(context, yield_setLastName);

          const yield_setMobile: GenericFormValidation_YieldEvent = {
            name: `${thisFieldIsland}_mobile`,
            validationEvent: ValidationEvent.onChange,
            value: thisCartAddress.telephone,
          };
          context.yield.validationEvent(context, yield_setMobile);

          const yield_setCountry: GenericFormValidation_YieldEvent = {
            name: `${thisFieldIsland}_manualAddressCountry`,
            validationEvent: ValidationEvent.onChange,
            value: thisCartAddress.country.code.toLowerCase(),
          };
          context.yield.validationEvent(context, yield_setCountry);

          if (thisCartAddress.street && thisCartAddress.street.length > 0) {
            const yield_setManualAddressLine1: GenericFormValidation_YieldEvent =
              {
                name: `${thisFieldIsland}_manualAddressLine1`,
                validationEvent: ValidationEvent.onChange,
                value: thisCartAddress.street[0],
              };
            context.yield.validationEvent(context, yield_setManualAddressLine1);
          }
          if (thisCartAddress.street && thisCartAddress.street.length > 1) {
            const yield_setManualAddressLine2: GenericFormValidation_YieldEvent =
              {
                name: `${thisFieldIsland}_manualAddressLine2`,
                validationEvent: ValidationEvent.onChange,
                value: thisCartAddress.street[1],
              };
            context.yield.validationEvent(context, yield_setManualAddressLine2);
          }

          const yield_setSuburb: GenericFormValidation_YieldEvent = {
            name: `${thisFieldIsland}_manualAddressSuburb`,
            validationEvent: ValidationEvent.onChange,
            value: thisCartAddress.city,
          };
          context.yield.validationEvent(context, yield_setSuburb);

          const yield_setPostcode: GenericFormValidation_YieldEvent = {
            name: `${thisFieldIsland}_manualAddressPostcode`,
            validationEvent: ValidationEvent.onChange,
            value: thisCartAddress.postcode,
          };
          context.yield.validationEvent(context, yield_setPostcode);

          if (thisCartAddress.region && thisCartAddress.region.code) {
            const yield_setState: GenericFormValidation_YieldEvent = {
              name: `${thisFieldIsland}_manualAddressState`,
              validationEvent: ValidationEvent.onChange,
              value: thisCartAddress.region.code,
            };
            context.yield.validationEvent(context, yield_setState);
          }
        }
      }

      if (
        thisFieldIsland === "shipping" &&
        lastFormattedAutocompletedAddress_forShipping &&
        (lastFormattedAutocompletedAddress_forShipping as string).length > 0
      ) {
        const yield_setAutocompleteAddressLabel: GenericFormValidation_YieldEvent =
          {
            name: `${thisFieldIsland}_autocompleteAddress`,
            validationEvent: ValidationEvent.onChange,
            value: lastFormattedAutocompletedAddress_forShipping,
          };
        context.yield.validationEvent(
          context,
          yield_setAutocompleteAddressLabel
        );
        const yield_setAutocompleteAddressValid: GenericFormValidation_YieldEvent =
          {
            name: `${thisFieldIsland}_autocompleteAddress${FormFieldExtensionKeys_AddressAutocomplete.SelectedFromAutocomplete}`,
            validationEvent: ValidationEvent.onChange,
            value: true,
          };
        context.yield.validationEvent(
          context,
          yield_setAutocompleteAddressValid
        );
      } else if (
        thisFieldIsland === "billing" &&
        lastFormattedAutocompletedAddress_forBilling &&
        (lastFormattedAutocompletedAddress_forBilling as string).length > 0
      ) {
        const yield_setAutocompleteAddressLabel: GenericFormValidation_YieldEvent =
          {
            name: `${thisFieldIsland}_autocompleteAddress`,
            validationEvent: ValidationEvent.onChange,
            value: lastFormattedAutocompletedAddress_forBilling,
          };
        context.yield.validationEvent(
          context,
          yield_setAutocompleteAddressLabel
        );
        const yield_setAutocompleteAddressValid: GenericFormValidation_YieldEvent =
          {
            name: `${thisFieldIsland}_autocompleteAddress${FormFieldExtensionKeys_AddressAutocomplete.SelectedFromAutocomplete}`,
            validationEvent: ValidationEvent.onChange,
            value: true,
          };
        context.yield.validationEvent(
          context,
          yield_setAutocompleteAddressValid
        );
      }
      const yield_setHasMigratedAutofills: GenericFormValidation_YieldEvent = {
        name: `${context.name}${FormFieldExtensionKeys_ShippingAutofill.HasMigratedAutofills}`,
        validationEvent: ValidationEvent.onChange,
        value: true,
      };
      context.yield.validationEvent(context, yield_setHasMigratedAutofills);
    }
    if (needsANewInitializationBecauseTheUserLoggedIn) {
      const yield_setHasMigratedNewUserLogInToTheInMemoryForm: GenericFormValidation_YieldEvent =
        {
          name: `${context.name}${FormFieldExtensionKeys_ShippingAutofill.HasMigratedAutofillsForAFreshUserLogin}`,
          validationEvent: ValidationEvent.onChange,
          value: true,
        };
      context.yield.validationEvent(
        context,
        yield_setHasMigratedNewUserLogInToTheInMemoryForm
      );
    }
  },

  validationEvents: [ValidationEvent.onSubmit],
  beforeValidate: genericBeforeValidate_removeDefaultKeyedError,
  validate: () => {},
});
